import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import api from '../../utils/api';
import { toast } from 'sonner';
import { AlertCircle, Info, AlertTriangle, XCircle, RefreshCw, Search, Loader2, FileText, Sparkles, Globe } from 'lucide-react';

const EventViewerTab = ({ agentId }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [logType, setLogType] = useState('Application');
  const [levelFilter, setLevelFilter] = useState('all');
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [showAiDialog, setShowAiDialog] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, [agentId, logType]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/agents/${agentId}/events?log=${logType}&limit=250`);
      const eventData = res.data.events || [];
      
      // Debug: Log event levels
      console.log('Events received:', eventData.length);
      const levelCounts = eventData.reduce((acc, e) => {
        acc[e.level] = (acc[e.level] || 0) + 1;
        return acc;
      }, {});
      console.log('Event levels:', levelCounts);
      
      setEvents(eventData);
    } catch (error) {
      toast.error('Failed to load event logs');
      console.error('Event fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const analyzeEventWithAI = async (event) => {
    setAiAnalyzing(true);
    setShowAiDialog(true);
    setAiAnalysis(null);
    
    try {
      const res = await api.post('/ai/analyze-event', {
        level: event.level,
        source: event.source,
        event_id: event.event_id,
        message: event.message,
        time_generated: event.time_generated
      });
      
      setAiAnalysis(res.data.analysis);
    } catch (error) {
      console.error('AI analysis error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'AI analysis failed';
      toast.error(errorMsg);
      setShowAiDialog(false);
    } finally {
      setAiAnalyzing(false);
    }
  };

  const searchEventOnGoogle = (event) => {
    // Create a concise search query - Event ID + Source + First 50 chars of message
    const messagePreview = event.message.substring(0, 50).trim();
    const searchQuery = `Windows Event ID ${event.event_id} ${event.source} ${messagePreview}`;
    const googleUrl = `https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`;
    window.open(googleUrl, '_blank', 'width=1200,height=800');
  };

  const getLevelIcon = (level) => {
    switch (level?.toLowerCase()) {
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'information':
        return <Info className="w-4 h-4 text-blue-500" />;
      default:
        return <FileText className="w-4 h-4 text-gray-500" />;
    }
  };

  const getLevelBadge = (level) => {
    switch (level?.toLowerCase()) {
      case 'error':
        return <Badge variant="destructive">Error</Badge>;
      case 'warning':
        return <Badge className="bg-yellow-500">Warning</Badge>;
      case 'information':
        return <Badge className="bg-blue-500">Info</Badge>;
      default:
        return <Badge variant="outline">{level}</Badge>;
    }
  };

  const filteredEvents = events.filter(event => {
    const matchesSearch = 
      event.message?.toLowerCase().includes(search.toLowerCase()) ||
      event.source?.toLowerCase().includes(search.toLowerCase()) ||
      event.event_id?.toString().includes(search);
    
    const matchesLevel = levelFilter === 'all' || 
      event.level?.toLowerCase() === levelFilter.toLowerCase();
    
    return matchesSearch && matchesLevel;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <Select value={logType} onValueChange={setLogType}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Application">Application</SelectItem>
            <SelectItem value="System">System</SelectItem>
            <SelectItem value="Security">Security</SelectItem>
          </SelectContent>
        </Select>

        <Select value={levelFilter} onValueChange={setLevelFilter}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Levels</SelectItem>
            <SelectItem value="error">Error</SelectItem>
            <SelectItem value="warning">Warning</SelectItem>
            <SelectItem value="information">Information</SelectItem>
          </SelectContent>
        </Select>

        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search events..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        <Button onClick={fetchEvents} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {logType} Events ({filteredEvents.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {filteredEvents.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {search || levelFilter !== 'all' ? 'No events match your filters' : 'No events found'}
              </div>
            ) : (
              filteredEvents.map((event, index) => (
                <div
                  key={index}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1">
                      {getLevelIcon(event.level)}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {getLevelBadge(event.level)}
                          <span className="text-sm font-medium text-gray-700">
                            Event ID: {event.event_id}
                          </span>
                          <span className="text-xs text-gray-500">
                            {event.source}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 mb-2">
                          {event.message}
                        </p>
                        <div className="text-xs text-gray-500">
                          {new Date(event.time_generated).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => analyzeEventWithAI(event)}
                        className="gap-1 whitespace-nowrap"
                        title="AI Analysis"
                      >
                        <Sparkles className="w-3 h-3" />
                        AI
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => searchEventOnGoogle(event)}
                        className="gap-1 whitespace-nowrap"
                        title="Google Search"
                      >
                        <Globe className="w-3 h-3" />
                        Search
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={showAiDialog} onOpenChange={setShowAiDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              AI Event Analysis
            </DialogTitle>
            <DialogDescription>
              AI-powered analysis of the selected event
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            {aiAnalyzing ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                <span className="ml-2">Analyzing event...</span>
              </div>
            ) : aiAnalysis ? (
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium mb-2">Analysis Result:</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{aiAnalysis}</p>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No analysis available
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventViewerTab;
